import {CSSProperties, RefObject, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {AdConfigKey} from '~/ads/getAdConfig';
import {CardNew} from '~/common/canvas/Card/CardNew';
import {GoogleAdBanner} from '~/common/fabric/GoogleAds/GoogleAdBanner';
import {
  selectCardWidthHeight,
  selectIsHostViewing,
} from '~/common/fabric/reducers/eviteEvent/selectors';
import {FabricArt} from '~/common/fabric/types';
import {selectIsHost} from '~/common/fabric/User/reducers/user';
import {WRAPPER_PADDING_MW} from '~/ViewInvitation/common/CardAnimation/animation-config';
import {useAnimationSlopes} from '~/ViewInvitation/common/CardAnimation/hooks/useAnimationSlopes';
import {useScrollToMeganav} from '~/ViewInvitation/hooks/useScrollToMeganav';
import {isPreview} from '~/ViewInvitation/utils';
import {useIsLargeAndUp} from '~library/hooks/useMatchQuery';

import './FreeCardWrapper.sass';

interface FreeCardWrapperProps {
  designTemplateRef: RefObject<HTMLDivElement | null>;
  fabricArt: FabricArt;
  toggleUpsellModal?: (show: boolean, source: string) => void;
  isPremium: boolean;
  googleAdsKey?: string;
  adsPageType?: string;
  scrollToPhotos: boolean;
  isViewInvitationPage?: boolean;
}

export const FreeCardWrapper = ({
  designTemplateRef,
  fabricArt,
  toggleUpsellModal,
  isPremium,
  googleAdsKey,
  adsPageType,
  scrollToPhotos,
  isViewInvitationPage,
}: FreeCardWrapperProps) => {
  const isCreateEditPreview = !!isPreview();
  const isHost = useSelector(selectIsHost);
  const isHostViewing = useSelector(selectIsHostViewing);
  const isLargeAndUp = useIsLargeAndUp();
  const [offsetTop, setOffsetTop] = useState(0);

  const searchParams = new URLSearchParams(window.location.search);
  const isGuestPreview = !!searchParams.get('preview');
  const isRealViewInvitation = !isCreateEditPreview && !isGuestPreview;
  const showSidePanel = !isPremium && isLargeAndUp && (isGuestPreview || isRealViewInvitation);

  const {width: cardW, height: cardH} = useSelector(selectCardWidthHeight);
  const cardAspectRatio = cardH / cardW;
  const {wrapperPaddingDesk} = useAnimationSlopes({cardAspectRatio});

  // biome-ignore lint/correctness/useExhaustiveDependencies: smh
  useEffect(() => {
    const offset = designTemplateRef?.current?.getBoundingClientRect().top ?? 0;
    setOffsetTop(offset);
  }, []);

  const {meganavHeight} = useScrollToMeganav({
    isViewInvitationPage,
    scrollToPhotos,
  });

  const style: CSSProperties & Record<string, string | number> = {
    '--wrapper-offset-top': `${offsetTop - (meganavHeight ?? 0)}px`,
    '--wrapper-padding-desk': `${wrapperPaddingDesk / 2}px`,
    '--wrapper-padding-mw': `${WRAPPER_PADDING_MW / 2}px`,
  };

  return (
    <div className="view-invitation__free-card-wrapper" id="invitation-card-asset" style={style}>
      <CardNew fabricArt={fabricArt} isFree />
      {showSidePanel && (
        <div className="view-invitation__side-panel">
          <GoogleAdBanner
            index={0}
            configKey={AdConfigKey.SIDE_PANEL}
            isCreateEditPreview={isCreateEditPreview}
            isGuestPreview={isGuestPreview}
            removeOnClick={() => toggleUpsellModal?.(true, 'remove-ads')}
            showRemoveButton={isHost || isHostViewing}
            location="rr"
            key={googleAdsKey}
            pageType={adsPageType}
          />
        </div>
      )}
    </div>
  );
};
